/**
 * MUI Theme Configuration
 * This file creates a Material-UI theme that integrates with our dynamic theming system.
 * All MUI components will automatically use colors from src/config/theme.js
 * 
 * @typedef {import('../types/theme').ThemeConfig} ThemeConfig
 */

import { createTheme } from '@mui/material/styles';
import { THEME } from './theme';

/**
 * Creates a MUI theme based on our theme configuration
 * @param {ThemeConfig} themeConfig - Our theme configuration
 * @returns {import('@mui/material').Theme} MUI theme object
 */
export const createMuiTheme = (themeConfig = THEME) => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: themeConfig.colors.primary,
        light: themeConfig.colors.success,
        dark: themeConfig.colors.textStrong,
        contrastText: themeConfig.colors.white,
      },
      secondary: {
        main: themeConfig.colors.secondary,
        light: themeConfig.colors.info,
        dark: themeConfig.colors.textPrimary,
        contrastText: themeConfig.colors.white,
      },
      error: {
        main: themeConfig.colors.danger,
        light: '#f44336',
        dark: '#d32f2f',
        contrastText: themeConfig.colors.white,
      },
      warning: {
        main: themeConfig.colors.warning,
        light: '#ff9800',
        dark: '#f57c00',
        contrastText: themeConfig.colors.black,
      },
      info: {
        main: themeConfig.colors.info,
        light: '#03a9f4',
        dark: '#01579b',
        contrastText: themeConfig.colors.white,
      },
      success: {
        main: themeConfig.colors.success,
        light: '#4caf50',
        dark: '#388e3c',
        contrastText: themeConfig.colors.white,
      },
      grey: {
        50: themeConfig.colors.backgroundAlt,
        100: themeConfig.colors.background,
        200: themeConfig.colors.borderLight,
        300: themeConfig.colors.border,
        400: themeConfig.colors.textLight,
        500: themeConfig.colors.gray,
        600: themeConfig.colors.textSecondary,
        700: themeConfig.colors.textPrimary,
        800: themeConfig.colors.textStrong,
        900: themeConfig.colors.black,
      },
      text: {
        primary: themeConfig.colors.textPrimary,
        secondary: themeConfig.colors.textSecondary,
        disabled: themeConfig.colors.textLight,
      },
      background: {
        default: themeConfig.colors.background,
        paper: themeConfig.colors.backgroundAlt,
      },
      divider: themeConfig.colors.border,
    },
    typography: {
      fontFamily: themeConfig.typography.fontFamily,
      fontSize: parseInt(themeConfig.typography.fontSizeBase),
      fontWeightLight: 300,
      fontWeightRegular: parseInt(themeConfig.typography.fontWeightNormal),
      fontWeightMedium: 500,
      fontWeightBold: parseInt(themeConfig.typography.fontWeightBold),
      h1: {
        fontSize: '2.5rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: parseInt(themeConfig.typography.fontWeightBold),
        color: themeConfig.colors.textPrimary,
      },
      body1: {
        fontSize: themeConfig.typography.fontSizeBase,
        color: themeConfig.colors.textPrimary,
      },
      body2: {
        fontSize: themeConfig.typography.fontSizeSmall,
        color: themeConfig.colors.textSecondary,
      },
    },
    spacing: 8, // Base spacing unit (8px)
    shape: {
      borderRadius: parseInt(themeConfig.borderRadius.md),
    },
    components: {
      // Button customization
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: themeConfig.borderRadius.md,
            fontWeight: themeConfig.typography.fontWeightNormal,
            padding: `${themeConfig.spacing.sm} ${themeConfig.spacing.md}`,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
          },
          containedPrimary: {
            backgroundColor: themeConfig.colors.primary,
            color: themeConfig.colors.white,
            '&:hover': {
              backgroundColor: themeConfig.colors.textStrong,
              boxShadow: '0 4px 8px rgba(11, 126, 181, 0.3)',
            },
            '&:active': {
              backgroundColor: themeConfig.colors.textStrong,
            },
          },
          containedSecondary: {
            backgroundColor: themeConfig.colors.secondary,
            color: themeConfig.colors.white,
            '&:hover': {
              backgroundColor: themeConfig.colors.textPrimary,
              boxShadow: '0 4px 8px rgba(27, 42, 61, 0.3)',
            },
            '&:active': {
              backgroundColor: themeConfig.colors.textPrimary,
            },
          },
          outlined: {
            borderColor: themeConfig.colors.border,
            color: themeConfig.colors.textPrimary,
            '&:hover': {
              borderColor: themeConfig.colors.primary,
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.04)`,
            },
          },
        },
      },
      // IconButton customization
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: themeConfig.colors.textSecondary,
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
              color: themeConfig.colors.primary,
            },
          },
          colorPrimary: {
            color: themeConfig.colors.primary,
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.12)`,
            },
          },
          colorSecondary: {
            color: themeConfig.colors.secondary,
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.secondary)}, 0.12)`,
            },
          },
        },
      },
      // DataGrid customization
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${themeConfig.colors.border}`,
            borderRadius: themeConfig.borderRadius.md,
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${themeConfig.colors.borderLight}`,
              color: themeConfig.colors.textPrimary,
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: themeConfig.colors.secondary,
              color: themeConfig.colors.white,
              borderBottom: `2px solid ${themeConfig.colors.border}`,
              fontSize: themeConfig.typography.fontSizeBase,
              fontWeight: themeConfig.typography.fontWeightBold,
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus, &:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: themeConfig.typography.fontWeightBold,
              color: themeConfig.colors.white,
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.04)`,
              },
              '&.Mui-selected': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
                '&:hover': {
                  backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.12)`,
                },
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${themeConfig.colors.border}`,
              backgroundColor: themeConfig.colors.background,
            },
            '& .MuiTablePagination-root': {
              color: themeConfig.colors.textPrimary,
            },
            '& .MuiDataGrid-sortIcon': {
              color: themeConfig.colors.white,
            },
            '& .MuiDataGrid-menuIconButton': {
              color: themeConfig.colors.white,
            },
          },
        },
      },
      // TextField customization
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: themeConfig.colors.border,
              },
              '&:hover fieldset': {
                borderColor: themeConfig.colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: themeConfig.colors.primary,
              },
            },
            '& .MuiInputLabel-root': {
              color: themeConfig.colors.textSecondary,
              '&.Mui-focused': {
                color: themeConfig.colors.primary,
              },
            },
          },
        },
      },
      // Select customization
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeConfig.colors.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: themeConfig.colors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: themeConfig.colors.primary,
            },
          },
        },
      },
      // Chip customization
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: themeConfig.borderRadius.sm,
          },
          colorPrimary: {
            backgroundColor: themeConfig.colors.primary,
            color: themeConfig.colors.white,
          },
          colorSecondary: {
            backgroundColor: themeConfig.colors.secondary,
            color: themeConfig.colors.white,
          },
          outlined: {
            borderColor: themeConfig.colors.border,
            color: themeConfig.colors.textPrimary,
          },
        },
      },
      // Card customization
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: themeConfig.borderRadius.md,
            border: `1px solid ${themeConfig.colors.border}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          },
        },
      },
      // Paper customization
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: themeConfig.borderRadius.md,
          },
          outlined: {
            border: `1px solid ${themeConfig.colors.border}`,
          },
        },
      },
      // Switch customization
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: 2,
              transitionDuration: '300ms',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: themeConfig.colors.white,
                '& + .MuiSwitch-track': {
                  backgroundColor: themeConfig.colors.primary,
                  opacity: 1,
                  border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.5,
                },
              },
              '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: themeConfig.colors.primary,
                border: `6px solid ${themeConfig.colors.white}`,
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                color: themeConfig.colors.gray,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.3,
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 22,
              height: 22,
            },
            '& .MuiSwitch-track': {
              borderRadius: 26 / 2,
              backgroundColor: themeConfig.colors.gray,
              opacity: 1,
              transition: 'background-color 300ms',
            },
          },
        },
      },
      // Checkbox customization
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: themeConfig.colors.border,
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
            },
            '&.Mui-checked': {
              color: themeConfig.colors.primary,
              '&:hover': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.12)`,
              },
            },
            '&.Mui-disabled': {
              color: themeConfig.colors.borderLight,
            },
          },
        },
      },
      // Radio customization
      MuiRadio: {
        styleOverrides: {
          root: {
            color: themeConfig.colors.border,
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
            },
            '&.Mui-checked': {
              color: themeConfig.colors.primary,
              '&:hover': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.12)`,
              },
            },
            '&.Mui-disabled': {
              color: themeConfig.colors.borderLight,
            },
          },
        },
      },
      // Tooltip customization
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: themeConfig.colors.textStrong,
            color: themeConfig.colors.white,
            fontSize: themeConfig.typography.fontSizeSmall,
            borderRadius: themeConfig.borderRadius.sm,
          },
          arrow: {
            color: themeConfig.colors.textStrong,
          },
        },
      },
      // Dialog customization
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: themeConfig.borderRadius.lg,
          },
        },
      },
      // AppBar customization
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: themeConfig.colors.headerBg,
            color: themeConfig.colors.white,
          },
        },
      },
      // Drawer customization
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: themeConfig.colors.sidebarBg,
            color: themeConfig.colors.white,
          },
        },
      },
      // Table customization
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: themeConfig.colors.secondary,
              color: themeConfig.colors.white,
              fontWeight: themeConfig.typography.fontWeightBold,
              borderBottom: `2px solid ${themeConfig.colors.border}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${themeConfig.colors.borderLight}`,
            color: themeConfig.colors.textPrimary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.04)`,
            },
            '&.Mui-selected': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
              '&:hover': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.12)`,
              },
            },
          },
        },
      },
      // Pagination customization
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              color: themeConfig.colors.textPrimary,
              borderColor: themeConfig.colors.border,
              '&:hover': {
                backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
                borderColor: themeConfig.colors.primary,
              },
              '&.Mui-selected': {
                backgroundColor: themeConfig.colors.primary,
                color: themeConfig.colors.white,
                borderColor: themeConfig.colors.primary,
                '&:hover': {
                  backgroundColor: themeConfig.colors.textStrong,
                  borderColor: themeConfig.colors.textStrong,
                },
              },
            },
          },
        },
      },
      // Tabs customization
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `2px solid ${themeConfig.colors.borderLight}`,
            minHeight: 48,
          },
          indicator: {
            backgroundColor: themeConfig.colors.primary,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 48,
            fontWeight: themeConfig.typography.fontWeightNormal,
            fontSize: themeConfig.typography.fontSizeBase,
            color: themeConfig.colors.textSecondary,
            '&:hover': {
              color: themeConfig.colors.primary,
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.04)`,
            },
            '&.Mui-selected': {
              color: themeConfig.colors.primary,
              fontWeight: themeConfig.typography.fontWeightBold,
            },
            '&.Mui-focusVisible': {
              backgroundColor: `rgba(${hexToRgb(themeConfig.colors.primary)}, 0.08)`,
            },
          },
        },
      },
      // Alert customization
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: `rgba(${hexToRgb(themeConfig.colors.success)}, 0.1)`,
            color: themeConfig.colors.success,
          },
          standardError: {
            backgroundColor: `rgba(${hexToRgb(themeConfig.colors.danger)}, 0.1)`,
            color: themeConfig.colors.danger,
          },
          standardWarning: {
            backgroundColor: `rgba(${hexToRgb(themeConfig.colors.warning)}, 0.1)`,
            color: themeConfig.colors.warning,
          },
          standardInfo: {
            backgroundColor: `rgba(${hexToRgb(themeConfig.colors.info)}, 0.1)`,
            color: themeConfig.colors.info,
          },
        },
      },
    },
  });
};

// Helper function to convert hex to RGB (same as in theme.js)
function hexToRgb(hex) {
  const normalized = hex.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized.split("").map((v) => `${v}${v}`).join("")
      : normalized;
  const bigint = parseInt(fullHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}

// Create default MUI theme
export const muiTheme = createMuiTheme(THEME);

/**
 * Update MUI theme at runtime
 * Call this when the theme changes to update all MUI components
 * @param {ThemeConfig} newTheme - New theme configuration
 * @returns {import('@mui/material').Theme} Updated MUI theme
 */
export const updateMuiTheme = (newTheme) => {
  return createMuiTheme(newTheme);
};
